// Google Analytics event tracking utilities using @next/third-parties
import { sendGAEvent } from "@next/third-parties/google";

// ============================================================================
// Pre-defined events for CitationEasy
// ============================================================================

/**
 * Track when a citation is generated
 */
export function trackCitationGenerated(
  style: string,
  inputType:
    | "url"
    | "doi"
    | "isbn"
    | "pmid"
    | "arxiv"
    | "bibtex"
    | "book-search"
    | "works-search"
    | "manual"
) {
  sendGAEvent("event", "citation_generated", {
    style,
    input_type: inputType,
  });
}

/**
 * Track when a citation style is changed
 */
export function trackStyleChange(style: string) {
  sendGAEvent("event", "style_changed", { style });
}

/**
 * Track when a citation is saved to bibliography
 */
export function trackCitationSaved(style: string) {
  sendGAEvent("event", "citation_saved", { style });
}

/**
 * Track when a bibliography is exported
 */
export function trackBibliographyExport(
  format:
    | "text"
    | "bibtex"
    | "ris"
    | "csljson"
    | "word"
    | "numbered"
    | "bulleted"
    | "rtf"
    | "html"
    | "annotated-text"
    | "annotated-word"
    | "print"
    | "share-link"
) {
  sendGAEvent("event", "bibliography_exported", { format });
}

/**
 * Track when references are imported into a bibliography
 */
export function trackBibliographyImport(
  format: "bibtex" | "ris" | "csljson",
  count: number
) {
  sendGAEvent("event", "bibliography_imported", { format, count });
}

/**
 * Track a whole-bibliography reformat into a different citation style.
 * `count` is how many citations actually changed, not how many were attempted.
 */
export function trackBibliographyRestyled(style: string, count: number) {
  sendGAEvent("event", "bibliography_restyled", { style, count });
}

/**
 * Track copying an in-text citation from a saved entry. `withLocator` records whether
 * a page/chapter pin cite was included.
 */
export function trackInTextCopied(style: string, withLocator: boolean) {
  sendGAEvent("event", "in_text_copied", { style, with_locator: withLocator });
}

/** Track saving an annotation on a saved citation. */
export function trackCitationNoteSaved(words: number) {
  sendGAEvent("event", "citation_note_saved", { words });
}

/**
 * Track when a new bibliography is created
 */
export function trackBibliographyCreated() {
  sendGAEvent("event", "bibliography_created", {});
}

/**
 * Track book search
 */
export function trackBookSearch(query: string) {
  sendGAEvent("event", "book_search", {
    search_term: query.slice(0, 50), // Truncate for privacy
  });
}

/**
 * Track when a book is selected from search
 */
export function trackBookSelected(title: string) {
  sendGAEvent("event", "book_selected", {
    book_title: title.slice(0, 50),
  });
}

/**
 * Track article (Crossref works) search
 */
export function trackWorksSearch(query: string) {
  sendGAEvent("event", "works_search", {
    search_term: query.slice(0, 50), // Truncate for privacy
  });
}

/**
 * Track when an article is selected from search
 */
export function trackWorkSelected(title: string) {
  sendGAEvent("event", "work_selected", {
    work_title: title.slice(0, 50),
  });
}

/**
 * Track citation wizard usage
 */
export function trackWizardOpened() {
  sendGAEvent("event", "wizard_opened", {});
}

/**
 * Track wizard completion
 */
export function trackWizardCompleted() {
  sendGAEvent("event", "wizard_completed", {});
}

/**
 * Track citation copied to clipboard
 */
export function trackCitationCopied(style: string) {
  sendGAEvent("event", "citation_copied", { style });
}

/**
 * Track style browser page views
 */
export function trackStylePageView(style: string) {
  sendGAEvent("event", "style_page_view", { style });
}

/**
 * Track theme toggle
 */
export function trackThemeToggle(theme: "light" | "dark") {
  sendGAEvent("event", "theme_toggled", { theme });
}
